/**
 * ScheduleCalendar
 * Feature: 005-clinic-scheduling (T019, T032, T033)
 *
 * FullCalendar wrapper with day/week/month views, PT-BR locale, and custom styles.
 */

import { useRef, useCallback, useEffect } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import ptBrLocale from '@fullcalendar/core/locales/pt-br'
import { format } from 'date-fns'
import { Loader2 } from 'lucide-react'
import { useCalendarEvents } from '../hooks/use-calendar-events'
import type { CalendarEventExtendedProps } from '../types/scheduling.types'

export interface SlotClickInfo {
  date: string
  startTime: string
  endTime: string
}

interface ScheduleCalendarProps {
  onEventClick?: (eventProps: CalendarEventExtendedProps) => void
  onSlotClick?: (info: SlotClickInfo) => void
  professionalFilter?: string | null
}

export function ScheduleCalendar({
  onEventClick,
  onSlotClick,
  professionalFilter,
}: ScheduleCalendarProps) {
  const calendarRef = useRef<FullCalendar>(null)
  const {
    events,
    isLoading,
    setDateRange,
    setProfessionalFilter,
  } = useCalendarEvents()

  // Sync external professional filter via effect
  useEffect(() => {
    if (professionalFilter !== undefined) {
      setProfessionalFilter(professionalFilter ?? null)
    }
  }, [professionalFilter, setProfessionalFilter])

  const handleDatesSet = useCallback(
    (arg: { startStr: string; endStr: string }) => {
      setDateRange({
        start: format(new Date(arg.startStr), 'yyyy-MM-dd'),
        end: format(new Date(arg.endStr), 'yyyy-MM-dd'),
      })
    },
    [setDateRange]
  )

  const handleEventClick = useCallback(
    (info: { event: { extendedProps: Record<string, unknown> } }) => {
      onEventClick?.(info.event.extendedProps as unknown as CalendarEventExtendedProps)
    },
    [onEventClick]
  )

  const handleDateClick = useCallback(
    (info: { dateStr: string; date: Date }) => {
      if (!onSlotClick) return
      const clickedDate = info.date
      const dateStr = format(clickedDate, 'yyyy-MM-dd')
      const startHour = clickedDate.getHours()
      const startMin = clickedDate.getMinutes()
      const endDate = new Date(clickedDate)
      endDate.setMinutes(endDate.getMinutes() + 50)
      const startTime = `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')}`
      const endTime = `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`
      onSlotClick({ date: dateStr, startTime, endTime })
    },
    [onSlotClick]
  )

  return (
    <div className="relative">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}
      <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
        initialView="timeGridWeek"
        headerToolbar={{
          left: 'prev,next today',
          center: 'title',
          right: 'dayGridMonth,timeGridWeek,timeGridDay',
        }}
        locale={ptBrLocale}
        slotDuration="00:30:00"
        slotMinTime="06:00:00"
        slotMaxTime="22:00:00"
        slotEventOverlap
        allDaySlot={false}
        nowIndicator
        weekends
        height="auto"
        contentHeight="auto"
        events={events}
        datesSet={handleDatesSet}
        eventClick={handleEventClick}
        dateClick={handleDateClick}
        selectable
        selectMirror
        eventClassNames={(arg) => {
          const status = arg.event.extendedProps.status as string
          return status === 'cancelled' ? ['fc-event-cancelled'] : []
        }}
      />

      <style>{`
        .fc-event-cancelled {
          opacity: 0.5;
          text-decoration: line-through;
        }
        .fc .fc-timegrid-slot {
          height: 2.5em;
        }
        .fc .fc-event {
          cursor: pointer;
          border-radius: 4px;
          font-size: 0.8rem;
          padding: 2px 4px;
        }
        .fc .fc-toolbar-title {
          font-size: 1.2rem;
          font-weight: 600;
        }
        .fc .fc-button {
          font-size: 0.85rem;
        }
        .fc .fc-button-primary {
          background-color: hsl(var(--primary));
          border-color: hsl(var(--primary));
        }
        .fc .fc-button-primary:not(:disabled).fc-button-active,
        .fc .fc-button-primary:not(:disabled):active {
          background-color: hsl(var(--primary));
          border-color: hsl(var(--primary));
          opacity: 0.9;
        }
        .fc .fc-timegrid-event .fc-event-main {
          padding: 2px 4px;
        }
      `}</style>
    </div>
  )
}
